<template>
  <div class="admin-container">
    <sidebar
      v-if="showSidebar"
      :pages="pages"
    />
    <f7-app
      :params="f7params"
      :class="`${showSidebar ? 'page-sidebar' : ''}`"
    >
      <f7-view
        v-if="accountReady"
        id="main-view"
        url="/"
        :main="true"
        pushStateSeparator=""
        :pushStateRoot="getPushStateRoot"
        :pushState="true"
        @view:init="viewInit"
      />
    </f7-app>
    <portal-target name="modals" />
    <portal-target name="instance-picker" />
    <div
      v-if="workInProgress"
      class="w-screen h-screen absolute bg-white"
      style="z-index: 9999;"
    >
      <div class="mx-auto w-11/12 sm:w-9/12 sm:min-w-150 mt-2 sm:mt-12 max-w-4xl">
        <div class="sm:flex justify-center text-center mt-10">
          <img
            class="block mx-auto my-10 sm:w-2/6 w-4/6"
            src="@/assets/work_together.svg"
            alt="create account"
          >
          <div class="self-center sm:ml-2 text-2xl sm:text-4xl font-bold sm:text-left">
            <span>Actualmente trabajamos en mejoras para</span>
            <span class="text-purple-500"> Cuidarte</span>
          </div>
        </div>
        <div class="sm:ml-2 text-lg sm:text-xl mt-10 text-center">
          Pronto volveremos a estar en linea...
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as axios from 'axios'
import routes from './routes'
import { mapState } from 'vuex'
import moment from 'moment-timezone'
import { f7App, f7View } from 'framework7-vue'
import Sidebar from '@/components/sidebar'
import { db, apiURL, firebaseMessaging } from './utils/firebase'

export default {
  name: 'App',
  components: {
    Sidebar,
    f7App,
    f7View,
  },
  data () {
    return {
      invitationsBind: null,
      workInProgress: false,
      viewReady: false,
      appIsReady: false,
      updateToast: null,
      firstToast: false,
      bearerReady: false,
      token: null,
      updatedLastSeen: false,
      accountReady: false,
      pages: {},
      f7params: {
        id: 'co.cuidarte.app',
        name: 'Cuidarte',
        theme: 'md',
        touch: {
          fastClicks: true,
        },
        routes: routes(this.$store.state.auth),
        dialog: {
          buttonCancel: 'Cancelar',
          buttonOk: 'Ok',
        },
        popover: {
          on: {
            opened: () => {
              window.history.pushState('', '', '#!/?&modal')
            },
            closed: () => {
              if (!this.closeModalByBack) {
                window.history.back()
              }
              this.$store.commit('settings/setCloseModalByBack', false)
            },
          },
        },
      },
    }
  },
  computed: {
    getPushStateRoot () {
      return window.location.origin
    },
    ...mapState({
      uid: state => state.auth.uid,
      phoneDetails: state => state.auth.phoneDetails,
      phoneLogin: state => state.auth.phoneLogin,
      checkResult: state => state.auth.checkResult,
      jwt: state => state.auth.jwt,
      account: state => state.auth.account,
      isRecovering: state => state.auth.isRecovering,
      networkState: state => state.settings.networkState,
      isWifi: state => state.settings.isWifi,
      updateIsReady: state => state.settings.updateIsReady,
      os: state => state.settings.os,
      versionAPK: state => state.settings.versionAPK,
      showSidebar: state => state.auth.showSidebar,
    }),
  },
  watch: {
    uid: function () {
      if (this.uid === null) {
        this.accountReady = true
      } else {
        if (this.uid) {
          if (this.appIsReady) this.bindAccount()
        } else {
          // Account deleted or logged Out
          window.history.pushState('', '', `${process.env.BASE_URL}`)
          setTimeout(() => {
            window.location.reload(true)
          }, 500)
        }
      }
    },
    updateIsReady: function (updateVal) {
      if (updateVal && this.appIsReady) {
        this.showUpdateModal()
      }
    },
    networkState: function (newState) {
      if (newState) {
        window.addEventListener('offline', this.checkConnection)
        window.removeEventListener('online', this.checkConnection)
        if (this.firstToast) this.connectedToast.open()
        this.firstToast = true
      } else {
        window.addEventListener('online', this.checkConnection)
        window.removeEventListener('offline', this.checkConnection)
        this.diconnectedToast.open()
      }
    },
    jwt: function (newVal) {
      axios.defaults.headers.common.Authorization = 'Bearer ' + this.jwt
      this.$f7.request.setup({
        timeout: 30000,
        headers: {
          Authorization: 'Bearer ' + this.jwt,
        },
      })
      this.bearerReady = true
      if (this.accountReady) {
        if (!this.updatedLastSeen) this.updateLastSeen()
        if (firebaseMessaging) this.bindNotificationService()
      }
    },
    token: function (token) {
      this.saveToken(token)
    },
  },
  async mounted () {
    axios.defaults.baseURL = apiURL
    axios.defaults.timeout = 60000
    db.collection('general').doc('settings').onSnapshot(doc => {
      if (doc.exists) {
        this.workInProgress = doc.data().wip
      }
    })
    this.$f7ready((f7) => {
      if (typeof navigator.splashscreen !== 'undefined') navigator.splashscreen.hide()
      this.appIsReady = true
      this.diconnectedToast = f7.toast.create({
        text: 'Estas desconectado :(',
        closeTimeout: 5000,
        closeButton: true,
      })
      this.connectedToast = f7.toast.create({
        text: '¡Estas conectado!',
        closeTimeout: 5000,
        closeButton: true,
      })
      this.checkConnection()
      if (this.updateIsReady) {
        this.showUpdateModal()
      } else {
        if (this.uid) {
          this.bindAccount()
        } else {
          this.accountReady = true
        }
      }
    })
  },
  methods: {
    async saveToken (token) {
      if (token !== this.token) {
        try {
          const response = await axios({
            url: '/users/token/create',
            method: 'POST',
            data: {
              uid: this.uid,
              token,
            },
          })
          console.info(response)
        } catch (err) {
          console.error(err)
        }
      }
    },
    bindNotificationService () {
      firebaseMessaging.requestPermission().then(() => {
        firebaseMessaging.getToken().then(currentToken => {
          if (currentToken) {
            if (this.jwt) {
              this.saveToken(currentToken)
            }
            this.token = currentToken
          }
        }).catch(function (err) {
          console.error('An error occurred while retrieving token. ', err)
        })
        firebaseMessaging.onTokenRefresh(() => {
          firebaseMessaging.getToken().then(refreshedToken => {
            if (refreshedToken) {
              if (this.jwt) {
                this.saveToken(refreshedToken)
              }
              this.token = refreshedToken
            }
          }).catch(function (err) {
            console.error('Unable to retrieve refreshed token ', err)
          })
        })
      }).catch(function (err) {
        console.error('Unable to get permission to notify.', err)
      })
      firebaseMessaging.onMessage(payload => {
        this.$f7.toast.create({
          text: payload.notification.body,
          destroyOnClose: true,
          closeButton: true,
        }).open()
      })
    },
    showUpdateModal () {
      this.$f7.dialog.alert('Vamos a recargar la aplicación.', 'Contenido Actualizado', () => {
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (this.os && this.versionAPK) {
            window.history.pushState('', '', `${process.env.BASE_URL}#${this.os}/${this.versionAPK}`)
          } else {
            window.history.pushState('', '', process.env.BASE_URL)
          }
          setTimeout(() => {
            window.location.reload(true)
          }, 1000)
        })
        window.registration.waiting.postMessage('skipWaiting')
      })
    },
    checkConnection () {
      if (typeof cordova !== 'undefined') {
        // this.$store.commit('settings/setProp', { prop: 'isWifi', val: navigator.connection.type === Connection.WIFI })
        // this.$store.commit('settings/setProp', { prop: 'networkState', val: navigator.connection.type !== Connection.NONE })
      } else {
        this.$store.commit('settings/setProp', { prop: 'networkState', val: navigator.onLine })
      }
    },
    async updateLastSeen () {
      const lastSeen = this.account.lastSeen ? moment(this.account.lastSeen.toMillis()).tz('America/Bogota').startOf('day').format('x') : 0
      const today = moment.tz('America/Bogota').startOf('day').format('x')
      if (lastSeen - today !== 0) {
        this.updatedLastSeen = true
        try {
          const response = await axios({
            url: '/users/update/lastSeen',
            method: 'POST',
            data: {
              uid: this.uid,
            },
          })
          console.info(response)
        } catch (err) {
          console.error(err)
        }
      }
    },
    async bindAccount () {
      this.$f7.dialog.preloader('Conectando para cuidarte...')
      db.collection('users').doc(this.uid)
        .onSnapshot(async account => {
          if (account.exists) {
            this.$store.commit('auth/setAccount', account.data())
            if (typeof account.data().observer !== 'undefined' && account.data().observer.length > 0) {
              this.$store.commit('admin/setInstanceID', this.account.observer[0])
              this.pages = {
                registry: { name: 'Registros', icon: 'book-open', path: '/admin/access-registrations/' },
              }
            }
            if (account.data().accessPoint) {
              this.pages = {
                ...this.pages,
                'access-point': { name: 'Porteria', icon: 'video', path: '/access-point/' },
              }
            }
            if ((typeof account.data().administrator !== 'undefined' && account.data().administrator.length > 0) || account.data().support) {
              if (typeof account.data().administrator !== 'undefined') this.$store.commit('admin/setInstanceID', this.account.administrator[0])
              this.pages = {
                dashboard: { name: 'General', icon: 'grid', path: '/admin/dashboard/' },
                tags: { name: 'Etiquetas', icon: 'pricetags', path: '/admin/tags/' },
                persons: { name: 'Personas', icon: 'people', path: '/admin/persons/' },
                summary: { name: 'Resumen', icon: 'bar-chart', path: '/admin/summary/' },
                'access-control': { name: 'Reglas', icon: 'lock', path: '/admin/access-control/' },
                'update-instance': { name: 'Configuración', icon: 'settings', path: '/admin/update-instance/' },
                'api-settings': { name: 'Integración', icon: 'share-outline', path: '/admin/api-settings/' },
                registry: { name: 'Registros', icon: 'book-open', path: '/admin/access-registrations/' },
                customize: { name: 'Encuesta', icon: 'brush', path: '/admin/customize/' },
                campaigns: { name: 'Campañas', icon: 'paper-plane', path: '/admin/campaigns/' },
                devices: { name: 'Lectores', icon: 'eye-outline', path: '/admin/devices/' },
                'access-point': { name: 'Porteria', icon: 'video', path: '/access-point/' },
              }
            }
            if (account.data().support) {
              this.pages = {
                ...this.pages,
                hardware: { name: 'Hardware', icon: 'hard-drive-outline', path: '/admin/hardware/' },
              }
            }
            if (typeof account.data().accessibleInstances !== 'undefined' && account.data().accessibleInstances.length > 0) {
              this.$store.commit('access/setInstanceID', this.account.accessibleInstances[0])
            }
            if (typeof account.data().support !== 'undefined') {
              this.$store.commit('admin/setInstanceID', 'Oje6feo4mWYyEdZHFMbs')
            }
            const instanceIDs = [...(account.data().accessibleInstances || []), ...(account.data().administrator || [])]
            const instanceIDsJSON = {}
            for (const instanceID of instanceIDs) {
              instanceIDsJSON[instanceID] = true
            }
            const uniqueInstanceIDs = Object.keys(instanceIDsJSON)
            const promises = []
            let instancesInfo
            if (account.data().support) {
              const instances = await db.collection('instances').get()
              instancesInfo = instances.docs
            } else {
              for (const instanceID of uniqueInstanceIDs) {
                promises.push(db.collection('instances').doc(instanceID).get())
              }
              instancesInfo = await Promise.all(promises)
            }
            for (const instanceInfo of instancesInfo) {
              this.$store.commit('auth/setInstancesInfo', { instanceID: instanceInfo.id, data: instanceInfo.data() || {} })
            }
            if (this.bearerReady) {
              if (firebaseMessaging) this.bindNotificationService()
              if (!this.updatedLastSeen) this.updateLastSeen()
            }
            this.accountReady = true
            if (account.data().shouldSetPass || this.isRecovering) {
              if (this.viewReady) this.redirectPass()
            } else {
              if (account.data().phoneNumber) {
                this.invitationsBind = db.collection('invitations').doc(account.data().phoneNumber).onSnapshot(invitations => {
                  this.$store.commit('auth/setInvitations', invitations.data())
                  if (invitations.data()) {
                    if (invitations.data().accessibleInstances && invitations.data().accessibleInstances.length > 0) {
                      setTimeout(() => {
                        this.$f7.views.main.router.navigate('/invitations/', { reloadAll: true })
                      }, 2000)
                    }
                  }
                })
              }
              if (this.viewReady) {
                if (window.location.pathname.indexOf('/login') >= 0 || window.location.pathname.indexOf('/create/account') >= 0 || window.location.pathname.indexOf('/password') >= 0) {
                  this.$f7.views.main.router.navigate('/', { reloadAll: true })
                }
              }
            }
            this.$f7.dialog.close()
            if (account.data().support || (typeof account.data().administrator !== 'undefined' && account.data().administrator.length > 0)) {
              if (localStorage.getItem('allowPersistance') === null) {
                this.$f7.dialog.confirm(
                  'Guardaremos información en tu equipo para mejorar el rendimiento de la app.',
                  '¿Estás en un equipo confiable (solo tú lo usas)?',
                  () => {
                    localStorage.setItem('allowPersistance', true)
                    window.history.pushState('', '', process.env.BASE_URL)
                    setTimeout(() => {
                      window.location.reload()
                    }, 500)
                  },
                  () => {
                    localStorage.setItem('allowPersistance', false)
                  })
              }
            }
          } else {
            if (this.viewReady) {
              this.$f7.views.main.router.navigate('/create/account/', { reloadAll: true })
            }
          }
        })
    },
    viewInit () {
      this.$f7.views.main.on('routeChange', this.routeChange)
      if (this.disconnected) {
        this.$f7.views.main.router.navigate('/disconnected/')
      }
      this.viewReady = true
      if (this.uid && this.accountReady) {
        this.redirectPass()
      }
    },
    redirectPass () {
      if (this.account.shouldSetPass || this.isRecovering) {
        this.$f7.dialog.close()
        this.$f7.views.main.router.navigate('/password/')
      }
    },
    routeChange (newRoute, previousRoute, router) {
      this.$store.commit('settings/setPage', newRoute)
    },
  },
}
</script>

<style lang="scss">
.admin-container{
  display:flex;
}
.page-sidebar{
  flex: 1 0 auto;
  width: calc(100% - 220px) !important;
  height: 100vh;
}
.page-sidebar>#main-view>.page{
  width: 100%;
}
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  margin-top: 60px;
}
</style>
